import { useEffect, useState } from 'react';
import z, { ZodError } from 'zod';
import { Button, LabeledContainer, InputError } from '@schibsted-svp/react-ui';
import { usePlayer } from 'hooks/usePlayer';
import { TimeInput } from 'components/ui/Form/TimeInput';
import Tooltip from 'components/ui/Tooltip';
import config from 'config';
import type { Midroll } from './Midrolls';
import css from './MidrollsForm.module.scss';

function createValidator(duration: number, excluded: number[]) {
    return z
        .number()
        .min(0, 'Start time cannot be less than 0')
        .max(duration, 'Start time cannot be grater than video duration')
        .refine((value) => !excluded.includes(value), 'Provided start time is already added');
}

type MidrollsFormProps = {
    duration: number;
    excluded: number[];
    submitDisabled: boolean;
    onMidrollAdd: (item: Midroll) => void;
    isLimitReached?: boolean;
    isAudio?: boolean;
    isCuePointsDirty?: boolean;
};

const AD_BREAKS_LIMIT = config.podcasts.adBreaksLimit;

function InsertButton({ handleSubmit, disabled }: { handleSubmit: () => void; disabled: boolean }) {
    return (
        <Button type="button" size="small" onClick={handleSubmit} disabled={disabled}>
            Insert ad break
        </Button>
    );
}

export function MidrollsForm({
    duration,
    excluded,
    submitDisabled,
    onMidrollAdd,
    isLimitReached,
    isAudio,
    isCuePointsDirty,
}: MidrollsFormProps) {
    const [value, setValue] = useState(0);
    const [error, setError] = useState<string>();

    const player = usePlayer();

    const shouldBlockAdsInsertion = isAudio && isLimitReached;
    const handleValueChange = (newValue: number) => {
        setValue(Math.floor(newValue));
        setError(undefined);
    };

    useEffect(() => {
        setValue(player?.timeOnSeek);
    }, [player?.timeOnSeek]);

    const handleSubmit = () => {
        try {
            createValidator(duration, excluded).parse(value);
            onMidrollAdd({ timeline: value });
            setValue(0);
            setError(undefined);
        } catch (e) {
            if (e instanceof ZodError) {
                const formatted = e.format();
                // eslint-disable-next-line no-underscore-dangle
                const message = formatted._errors?.[0] ?? formatted[0]?._errors;
                setError(message);
            }
        }
    };

    return (
        <div className={css.container}>
            <LabeledContainer strict label="Start time">
                <TimeInput
                    size="small"
                    value={value}
                    maxValue={duration}
                    disabled={!player}
                    onValueChange={handleValueChange}
                />
            </LabeledContainer>

            <LabeledContainer>
                {shouldBlockAdsInsertion ? (
                    <Tooltip theme="light" content="Ad breaks limit is reached" placement="top" offset={[0, 12]}>
                        <span>
                            <InsertButton
                                handleSubmit={handleSubmit}
                                disabled={submitDisabled || !player || shouldBlockAdsInsertion}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <InsertButton
                        handleSubmit={handleSubmit}
                        disabled={submitDisabled || !player || shouldBlockAdsInsertion}
                    />
                )}
            </LabeledContainer>
            {isAudio ? <p className={css.maxAds}>You can add up to {AD_BREAKS_LIMIT} ad breaks</p> : null}
            {(error || (shouldBlockAdsInsertion && isCuePointsDirty)) && (
                <InputError className={css.error}>{error || "You've reached the limit of ad breaks number"}</InputError>
            )}
        </div>
    );
}
